/**
 * Iter 60 — Office Locations Settings Page.
 *
 * Lets HR / Admin / Super Admin add, edit, delete office locations with:
 *  - Name + Address
 *  - Latitude / Longitude (manual entry OR "Use my current location")
 *  - Geofence radius (slider, default 200m)
 *
 * Backed by /api/mobile/attendance/office-locations (GET / PUT).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, MapPin, Save, Briefcase, Loader2, Crosshair, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut, apiPost } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const blank = () => ({ id: '', name: '', address: '', lat: '', lng: '', radius_m: 200 });

export default function OfficeLocations() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(null);
  const [resolving, setResolving] = useState(null);
  const [linkInput, setLinkInput] = useState({});

  const canEdit =
    user?.role === 'super_admin' ||
    user?.role === 'admin' ||
    /hr|human/i.test(user?.department || '');

  useEffect(() => {
    apiGet('/mobile/attendance/office-locations')
      .then((r) => setItems((r.items || []).map((o) => ({ ...o }))))
      .catch(() => toast.error('Could not load office locations'))
      .finally(() => setLoading(false));
  }, []);

  const addRow = () => setItems((arr) => [...arr, blank()]);
  const removeRow = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const updRow = (idx, patch) =>
    setItems((arr) => arr.map((o, i) => (i === idx ? { ...o, ...patch } : o)));

  const captureCurrentLocation = (idx) => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
    setLocating(idx);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        updRow(idx, {
          lat: pos.coords.latitude.toFixed(6),
          lng: pos.coords.longitude.toFixed(6),
        });
        toast.success(`Location captured · ±${Math.round(pos.coords.accuracy)}m`);
        setLocating(null);
      },
      (err) => { toast.error(`GPS failed: ${err.message}`); setLocating(null); },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const resolveLink = async (idx) => {
    const url = (linkInput[idx] || '').trim();
    if (!url) { toast.error('Paste a Google Maps share link or "lat,lng" first'); return; }
    setResolving(idx);
    try {
      const r = await apiPost('/settings/resolve-location', { url });
      updRow(idx, { lat: r.lat.toFixed(6), lng: r.lng.toFixed(6) });
      setLinkInput((m) => ({ ...m, [idx]: '' }));
      toast.success(`Coordinates set: ${r.lat.toFixed(6)}, ${r.lng.toFixed(6)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resolve link');
    } finally { setResolving(null); }
  };

  const save = async () => {
    // Validate
    for (let i = 0; i < items.length; i++) {
      const o = items[i];
      if (!o.name?.trim()) { toast.error(`Row ${i + 1}: Name is required`); return; }
      if (o.lat === '' || o.lng === '' || isNaN(parseFloat(o.lat)) || isNaN(parseFloat(o.lng))) {
        toast.error(`Row ${i + 1}: Latitude & Longitude required`); return;
      }
    }
    setSaving(true);
    try {
      const payload = items.map((o) => ({
        id: o.id || undefined,
        name: o.name.trim(),
        address: (o.address || '').trim() || null,
        lat: parseFloat(o.lat),
        lng: parseFloat(o.lng),
        radius_m: parseInt(o.radius_m, 10) || 200,
      }));
      const r = await apiPut('/mobile/attendance/office-locations', payload);
      setItems((r.items || []).map((o) => ({ ...o })));
      toast.success(`${(r.items || []).length} office location(s) saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-[#3b4872]" /></div>;
  }

  return (
    <div>
      <button onClick={() => navigate('/app/settings')} className="flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-wider text-slate-500 hover:text-[#3b4872] mb-2" data-testid="ol-back-btn">
        <ArrowLeft size={13} /> Back to Settings
      </button>
      <PageHeader overline="Company Configuration" title="Office Locations"
        subtitle="HR & Admin can add the company's office locations with GPS coordinates and geofence radius. These appear in the mobile attendance picker so employees can punch in from the right office." />

      <div className="bg-white border border-slate-200 rounded-sm">
        <header className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Briefcase size={14} className="text-[#3b4872]" />
            <span className="text-xs font-display font-bold uppercase tracking-wider">{items.length} Office location{items.length === 1 ? '' : 's'} configured</span>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <button onClick={addRow} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider text-[#3b4872] border border-[#3b4872]/30 rounded-sm hover:bg-indigo-50" data-testid="ol-add-row">
                <Plus size={12} /> Add Office
              </button>
              <button onClick={save} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider bg-[#3b4872] text-white rounded-sm hover:bg-[#2d3856] disabled:opacity-50" data-testid="ol-save-all">
                <Save size={12} /> {saving ? 'Saving…' : 'Save All'}
              </button>
            </div>
          )}
        </header>

        <div className="p-4">
          {items.length === 0 && (
            <div className="text-center py-10 text-slate-400 text-xs uppercase tracking-widest font-display" data-testid="ol-empty">
              No office locations configured. Click <strong className="text-[#3b4872]">Add Office</strong> to add your first one.
            </div>
          )}
          <div className="space-y-3">
            {items.map((o, idx) => (
              <div key={o.id || `new-${idx}`} className="border border-slate-200 rounded-sm p-3 bg-slate-50/40" data-testid={`ol-row-${idx}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <Label>Office Name *</Label>
                    <Input value={o.name} onChange={(v) => updRow(idx, { name: v })} placeholder="e.g. Ellora HQ Pune · Hadapsar" disabled={!canEdit} testid={`ol-name-${idx}`} />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Address</Label>
                    <Input value={o.address || ''} onChange={(v) => updRow(idx, { address: v })} placeholder="Plot 42, Sector 5, Pune 411028" disabled={!canEdit} testid={`ol-address-${idx}`} />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Paste Google Maps link / "lat,lng"</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="text"
                        value={linkInput[idx] || ''}
                        onChange={(e) => setLinkInput((m) => ({ ...m, [idx]: e.target.value }))}
                        placeholder="https://share.google/... or 18.5204, 73.8567"
                        disabled={!canEdit}
                        className="flex-1 border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#3b4872] disabled:bg-slate-100"
                        data-testid={`ol-link-input-${idx}`}
                      />
                      {canEdit && (
                        <button
                          onClick={() => resolveLink(idx)}
                          disabled={resolving === idx}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider text-white bg-[#3b4872] rounded-sm hover:bg-[#2d3856] disabled:opacity-50"
                          data-testid={`ol-link-resolve-${idx}`}
                        >
                          <LinkIcon size={12} /> {resolving === idx ? 'Resolving…' : 'Use Link'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label>Latitude *</Label>
                    <Input value={o.lat} onChange={(v) => updRow(idx, { lat: v })} placeholder="18.504400" disabled={!canEdit} testid={`ol-lat-${idx}`} />
                  </div>
                  <div>
                    <Label>Longitude *</Label>
                    <Input value={o.lng} onChange={(v) => updRow(idx, { lng: v })} placeholder="73.947600" disabled={!canEdit} testid={`ol-lng-${idx}`} />
                  </div>
                  <div className="md:col-span-2 flex items-center gap-2">
                    {canEdit && (
                      <button onClick={() => captureCurrentLocation(idx)} disabled={locating === idx} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider text-[#3b4872] border border-[#3b4872]/30 rounded-sm hover:bg-indigo-50 disabled:opacity-50" data-testid={`ol-use-location-${idx}`}>
                        <Crosshair size={12} /> {locating === idx ? 'Locating…' : 'Use my current location'}
                      </button>
                    )}
                    {o.lat && o.lng && (
                      <a href={`https://www.google.com/maps?q=${o.lat},${o.lng}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-slate-500 hover:text-[#3b4872]" data-testid={`ol-map-link-${idx}`}>
                        <MapPin size={11} /> Open in Google Maps
                      </a>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <Label>Geofence Radius — <span className="text-[#3b4872] font-mono">{o.radius_m}m</span> <span className="text-slate-400 font-normal normal-case tracking-normal">(employees within this radius are marked on-site)</span></Label>
                    <input
                      type="range" min="10" max="2000" step="10"
                      value={o.radius_m}
                      onChange={(e) => updRow(idx, { radius_m: parseInt(e.target.value, 10) })}
                      disabled={!canEdit}
                      className="w-full mt-2 accent-[#3b4872]"
                      data-testid={`ol-radius-${idx}`}
                    />
                    <div className="flex justify-between text-[9px] text-slate-400 font-display font-bold uppercase tracking-widest mt-0.5">
                      <span>10m</span><span>500m</span><span>1km</span><span>2km</span>
                    </div>
                  </div>
                </div>
                {canEdit && (
                  <div className="flex justify-end mt-2">
                    <button onClick={() => removeRow(idx)} className="flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800 font-display font-semibold" data-testid={`ol-remove-${idx}`}>
                      <Trash2 size={12} /> Remove
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {!canEdit && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900">
          <strong>Read-only:</strong> only Super Admin, Admin and HR can edit office locations. Ask your administrator if these details need updating.
        </div>
      )}
    </div>
  );
}

function Label({ children }) {
  return <label className="text-[10px] font-display font-bold tracking-wider uppercase text-slate-500">{children}</label>;
}
function Input({ value, onChange, placeholder, disabled, testid }) {
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm mt-1 focus:outline-none focus:border-[#3b4872] disabled:bg-slate-100 disabled:text-slate-500"
      data-testid={testid}
    />
  );
}
